'use client';

import React from 'react';
import TickerLink from '@/components/ticker/TickerLink';
import { formatPct } from '@/lib/utils';

/** The ticker chip's resting blue tint.
 *
 *  Exported so a surface that needs the chip LOOK without this component's
 *  link wiring can match it exactly instead of re-typing the palette and
 *  drifting: the watchlist row is itself a TickerLink, so nesting another one
 *  inside it would be invalid. Hover is deliberately left out — the badge
 *  hovers on itself, while the watchlist chip hovers with its whole row
 *  (`group-hover:`), and baking either in would be wrong at the other site. */
export const TICKER_CHIP_TINT = 'bg-sky-500/10 text-sky-300';

interface TickerBadgeProps {
  ticker: string;
  /** Optional live change% rendered next to the symbol (colored). */
  changePct?: number | null;
  /** Prefix the symbol with `$` (news-tape style). */
  prefix?: boolean;
  /** Use a <button> instead of <a> — required when the badge sits inside an
   *  outer <a> (e.g. a news row) to avoid invalid nested anchors. */
  asButton?: boolean;
  /** Force navigation instead of the popup (rare). */
  navigateOnly?: boolean;
  size?: 'sm' | 'md';
  /** Extra classes — override the default tint (e.g. amber/pink). */
  className?: string;
}

/**
 * Clickable ticker BADGE — a pill that opens the in-app TickerPopup (which
 * renders a small chart + quote) on click. Shared across the news tiles and
 * the scanner so every ticker is a consistent, chart-on-click chip. Wraps
 * TickerLink (popup wiring + nested-anchor safety via asButton).
 */
export default function TickerBadge({
  ticker, changePct, prefix, asButton, navigateOnly, size = 'sm', className,
}: TickerBadgeProps) {
  const pct = changePct;
  const pctColor = pct == null ? '' : pct >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const sz = size === 'md' ? 'text-xs px-2 py-0.5' : 'text-[11px] px-1.5 py-0.5';
  const cls = [
    'inline-flex items-center gap-1 rounded-md font-mono font-semibold align-middle cursor-pointer transition-colors',
    `${TICKER_CHIP_TINT} hover:bg-sky-500/20`,
    sz,
    className,
  ].filter(Boolean).join(' ');
  return (
    <TickerLink ticker={ticker} asButton={asButton} navigateOnly={navigateOnly} className={cls}>
      <span>{prefix ? `$${ticker}` : ticker}</span>
      {pct != null && <span className={pctColor}>{formatPct(pct)}</span>}
    </TickerLink>
  );
}
